<template>
  <div class="job-action-column">
    <WdsMenu
      z-index="100"
      placement="bottom-end"
      manual-trigger
      :global="!isFullscreen"
      @menu:open="load"
    >
      <template #trigger="{ toggle }">
        <WdsButtonGhost
          :data-test="'contract-table-contract-row-actions-' + input.id"
          @click="toggle"
        >
          <WdsIcon name="overflow-menu-horizontal" :size="16" />
        </WdsButtonGhost>
      </template>

      <div v-if="column && !loading">
        <WdsMenuOption
          :href="
            laravelNamedRoute(namedRoutes.jobs.show, {
              job: column?.id ?? '',
            })
          "
        >
          {{ $t('Job Details') }}
        </WdsMenuOption>
        <template v-if="column.viewerCanEdit">
          <WdsMenuDivider />
          <WdsMenuTitle>
            {{ $t('Manage Job') }}
          </WdsMenuTitle>
          <WdsMenuOption
            :href="
              laravelNamedRoute(namedRoutes.yjob.edit, {
                yjob: column?.id ?? '',
                tab: JOB_TAB.OVERVIEW,
              })
            "
          >
            {{ $t('Overview') }}
          </WdsMenuOption>
          <WdsMenuOption
            :href="
              laravelNamedRoute(namedRoutes.yjob.edit, {
                yjob: column?.id ?? '',
                tab: JOB_TAB.DETAILS,
              })
            "
          >
            {{ $t('Details') }}
          </WdsMenuOption>
          <WdsMenuOption
            :href="
              laravelNamedRoute(namedRoutes.yjob.edit, {
                yjob: column?.id ?? '',
                tab: JOB_TAB.BUDGET,
              })
            "
          >
            {{ $t('Budget') }}
          </WdsMenuOption>
          <WdsMenuOption
            :href="
              laravelNamedRoute(namedRoutes.yjob.edit, {
                yjob: column?.id ?? '',
                tab: JOB_TAB.SETTINGS,
              })
            "
          >
            {{ $t('Settings') }}
          </WdsMenuOption>
        </template>
        <template v-if="column.viewerCanCreateHire">
          <WdsMenuDivider />
          <WdsMenuOption @click="() => (showTalentModal = true)">
            {{ $t('Create hire offer') }}
          </WdsMenuOption>
        </template>
        <template v-if="column.viewerCanEdit">
          <WdsMenuDivider />
          <WdsMenuOption
            theme="DESTRUCTIVE"
            :data-swaltext="
              $t('Do you want to terminate this job and terminate any ongoing job process?')
            "
            :data-swalconfirmtext="$t('Yes, mark the job as completed')"
            :data-swalcompletedtext="$t('The job closes now')"
            :data-formid="`form-job-close-${column.number}`"
            href="#"
            class="swal-delete"
          >
            <WdsStack gap-xs justify-center align-center>
              <WdsIcon name="trash-can" :size="16" />
              <span>{{ $t('End job') }}</span>
            </WdsStack>
          </WdsMenuOption>
          <BaseHtmlForm
            :id="`form-job-close-${column.number}`"
            :action="`/job/${column.number}/close`"
            method="PATCH"
            hidden
          />
        </template>
      </div>
      <div
        v-else
        class="tw-px-4 tw-py-2"
        :data-test="'contract-table-contract-row-actions-loading-' + input.id"
      >
        <WdsSpinner indeterminate />
      </div>
    </WdsMenu>
  </div>
  <teleport to=".graphql-table-globals-wrapper">
    <WdsModal
      lg
      :show="showTalentModal"
      :title="$t('Direct Hire')"
      data-test="trusted-contact-modal"
      tabindex="0"
      @cancel="showTalentModal = false"
    >
      <template #header>
        <div>
          <WdsHeading>{{ $t('Direct Hire') }}</WdsHeading>
          <WdsText large class="tw-mt-2">
            {{ $t('Offer new job to specific worker') }}
          </WdsText>
        </div>
      </template>
      <TalentSelectionDirectHire
        v-model:trusted-contact="trustedContact"
        @cancel="showTalentModal = false"
        @confirm="showTalentModal = false"
      />
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsHeading,
  WdsIcon,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsMenuTitle,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed, inject, ref, watch } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { FragmentType, graphql, useFragment } from '@/gql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { JOB_TAB } from '@/components/Job/constants'
import TalentSelectionDirectHire from '@/components/Job/Create/TalentSelectionDirectHire.vue'
import { TrustedContact } from '@/components/Job/hiring-types'
import { TableFullscreen } from '@/components/GraphqlDataTable/injection'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'

const JobActionDataRequest = graphql(`
  fragment JobActionDataRequest on Job {
    id
    number
    viewerCanEdit
    viewerCanDelete
    viewerCanCreateHire
  }
`)

const JobActionColumn = graphql(`
  fragment JobActionColumn on Job {
    id
    viewerCanEdit # For bulk actions
  }
`)

const props = defineProps<{ row: FragmentType<typeof JobActionColumn> }>()

const isFullscreen = inject(TableFullscreen, false)

const input = computed(() => useFragment(JobActionColumn, props.row))

const { result, loading, load } = useLazyQuery(
  graphql(`
    query JobActionColumnMenu($id: ID!) {
      job(id: $id) {
        ...JobActionDataRequest
      }
    }
  `),
  () => ({ id: input.value.id }),
  {
    clientId: 'publicApiCustomFields',
  },
)

const column = computed(() => useFragment(JobActionDataRequest, result.value?.job))
const showTalentModal = ref(false)
const trustedContact = ref<TrustedContact | undefined>(undefined)

watch(
  () => trustedContact.value,
  () => {
    window.location.href = `${laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
      freelancer: trustedContact.value?.workerId,
    })}?job=${column.value?.id}`
  },
  { once: true },
)
</script>

<style scoped lang="scss">
.job-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
